import React, { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Heart } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import userService, { FavouriteType } from '../../services/userService';

const ACCENT = '#3fced1';

/** Mapping FavouriteType → savedX-Array im User-Objekt */
const SAVED_KEY_BY_TYPE: Record<FavouriteType, string> = {
  TOURNAMENT: 'savedTournaments',
  SERIES: 'savedSeries',
  CLUB: 'savedClubs',
  PUB: 'savedPubs',
  SHOP: 'savedShops',
};

export interface FavoriteButtonProps {
  /** ID des Eintrags (Turnier, Serie, Verein, Spielstätte oder Shop) */
  id: string;
  /** Typ des Eintrags (exakte Backend-Werte, uppercase) */
  type: FavouriteType;
  /** Optional: zusätzliche CSS-Klassen */
  className?: string;
  /** Icon-Größe in px (Default 20) */
  size?: number;
  /** Optional: Label neben dem Herz anzeigen ("Gespeichert" / "Speichern") */
  showLabel?: boolean;
}

/**
 * Wiederverwendbarer Favoriten-Button (Herz).
 * - Liest den Gespeichert-Status aus den savedX-Arrays des Users im AuthContext
 * - Togglet via /api/user/favourites/save bzw. /remove (optimistic UI)
 * - Nicht eingeloggt → Link zur Login-Seite
 */
const FavoriteButton: React.FC<FavoriteButtonProps> = ({
  id,
  type,
  className = '',
  size = 20,
  showLabel = false,
}) => {
  const { user, isAuthenticated, refreshUser } = useAuth();
  const [optimistic, setOptimistic] = useState<boolean | null>(null);
  const [isBusy, setIsBusy] = useState(false);

  const isSavedOnServer = useMemo(() => {
    if (!user || !id) return false;
    const arr = (user as any)?.[SAVED_KEY_BY_TYPE[type]];
    return Array.isArray(arr) && arr.includes(id);
  }, [user, id, type]);

  const isSaved = optimistic ?? isSavedOnServer;

  if (!isAuthenticated) {
    return (
      <RouterLink
        to="/login"
        onClick={(e) => e.stopPropagation()}
        className={`inline-flex items-center gap-1.5 text-gray-400 hover:text-[#3fced1] transition-colors ${className}`}
        title="Anmelden, um Favoriten zu speichern"
        aria-label="Anmelden, um Favoriten zu speichern"
      >
        <Heart size={size} />
        {showLabel && <span className="text-sm">Speichern</span>}
      </RouterLink>
    );
  }

  const toggle = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isBusy || !id) return;

    const next = !isSaved;
    setOptimistic(next);
    setIsBusy(true);
    try {
      if (next) {
        await userService.saveFavourite(id, type);
      } else {
        await userService.removeFavourite(id, type);
      }
      await refreshUser();
      setOptimistic(null);
    } catch (err) {
      console.error('Favorit konnte nicht aktualisiert werden:', err);
      // Optimistic UI zurückrollen
      setOptimistic(null);
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={isBusy}
      className={`inline-flex items-center gap-1.5 transition-colors disabled:opacity-60 ${
        isSaved ? 'text-[#3fced1]' : 'text-gray-400 hover:text-[#3fced1]'
      } ${className}`}
      title={isSaved ? 'Aus Favoriten entfernen' : 'Zu Favoriten hinzufügen'}
      aria-label={isSaved ? 'Aus Favoriten entfernen' : 'Zu Favoriten hinzufügen'}
      aria-pressed={isSaved}
    >
      <Heart size={size} fill={isSaved ? ACCENT : 'none'} />
      {showLabel && (
        <span className="text-sm">{isSaved ? 'Gespeichert' : 'Speichern'}</span>
      )}
    </button>
  );
};

export default FavoriteButton;
